import React from 'react';
import moment from 'moment';
import { Box, Chip, Divider, Typography } from '@mui/material';
import { ThemeContext } from 'src/store/ContextStore';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import axios from 'src/axios';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';

const TYPE_COLOURS = {
  gail: '#354051',
  canrisk: '#354051',
  prs: '#354051'
};

const TYPE_LABELS = {
  gail: 'GAIL',
  canrisk: 'CanRisk',
  prs: 'PRS'
};

function getPrsSummary(prsResult) {
  if (!prsResult) return null;
  const parts = [];
  if (prsResult.zscore != null) parts.push(`z=${prsResult.zscore}`);
  if (prsResult.percentile != null)
    parts.push(`${prsResult.percentile}th percentile`);
  if (prsResult.relativeRisk != null)
    parts.push(`RR=${prsResult.relativeRisk}`);
  return parts.length ? parts.join(' | ') : null;
}

function getCanriskSummary(canriskResult) {
  try {
    const pedigreeResult = canriskResult?.pedigree_result?.[0];
    const tenYr =
      pedigreeResult?.ten_yr_cancer_risk?.[0]?.['breast cancer risk']?.percent;
    const lifetime =
      pedigreeResult?.lifetime_cancer_risk?.[0]?.['breast cancer risk']
        ?.percent;
    if (tenYr != null && lifetime != null) {
      return `10yr: ${tenYr}% | Lifetime: ${lifetime}%`;
    }
  } catch (_) {
    /* ignore */
  }
  return null;
}

function DetailRow({ label, value }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        py: 0.5
      }}
    >
      <Typography variant="body1" sx={{ mr: 2 }}>
        {label}
      </Typography>
      <Typography variant="body1">{String(value)}</Typography>
    </Box>
  );
}

function GailDetail({ record }) {
  const rows = [
    ['Score', record.gailRiskScore],
    ['Age', record.age],
    ['Race', record.race],
    ['Age at first menstruation', record.ageMen],
    ['Number of biopsies', record.nBiops],
    ['Age at first birth', record.ageFLB],
    ['1st degree relatives with breast cancer', record.numRel],
    [
      'Personal history of breast cancer / DCIS / LCIS',
      record.isBreastCancer != null
        ? record.isBreastCancer
          ? 'Yes'
          : 'No'
        : null
    ],
    [
      'Known BRCA1/BRCA2 mutation or hereditary syndrome',
      record.isMutation != null ? (record.isMutation ? 'Yes' : 'No') : null
    ]
  ].filter(([, v]) => v != null);

  return (
    <Box>
      {rows.map(([k, v]) => (
        <DetailRow key={k} label={k} value={v} />
      ))}
    </Box>
  );
}

function CanRiskDetail({ record }) {
  const result = record.canriskResult;
  if (!result) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary'
        }}
      >
        No scores calculated.
      </Typography>
    );
  }

  const pedigreeResult = result?.pedigree_result?.[0];
  const tenYrRisks = pedigreeResult?.ten_yr_cancer_risk?.[0] || {};
  const lifetimeRisks = pedigreeResult?.lifetime_cancer_risk?.[0] || {};
  const mutationProbs = pedigreeResult?.mutation_probabilties?.[0] || {};

  const riskRows = Object.keys(tenYrRisks).map((cancer) => ({
    cancer,
    tenYr: tenYrRisks[cancer]?.percent,
    lifetime: lifetimeRisks[cancer]?.percent
  }));

  const mutRows = Object.entries(mutationProbs);

  return (
    <Box>
      {riskRows.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Cancer Risk
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 80px 80px',
              columnGap: 1,
              mb: 1
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 700
              }}
            >
              Cancer
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 700,
                textAlign: 'right'
              }}
            >
              10-year
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 700,
                textAlign: 'right'
              }}
            >
              Lifetime
            </Typography>
            {riskRows.map(({ cancer, tenYr, lifetime }) => (
              <React.Fragment key={cancer}>
                <Typography
                  variant="body2"
                  sx={{ textTransform: 'capitalize' }}
                >
                  {cancer}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'right'
                  }}
                >
                  {tenYr != null ? `${tenYr}%` : '—'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'right'
                  }}
                >
                  {lifetime != null ? `${lifetime}%` : '—'}
                </Typography>
              </React.Fragment>
            ))}
          </Box>
        </>
      )}
      {mutRows.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" gutterBottom>
            Mutation Probabilities
          </Typography>
          {mutRows.map(([gene, val]) => (
            <Box
              key={gene}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                py: 0.25
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary'
                }}
              >
                {gene}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600
                }}
              >
                {val != null ? `${val.percent}%` : '—'}
              </Typography>
            </Box>
          ))}
        </>
      )}
      {result?.warnings?.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{
              color: 'warning.main'
            }}
          >
            Warnings
          </Typography>
          {result.warnings.map((w, i) => (
            <Typography
              key={i}
              variant="body2"
              sx={{
                color: 'warning.main'
              }}
            >
              {w}
            </Typography>
          ))}
        </>
      )}
    </Box>
  );
}

export function RecordDetailModal({ record, dateFormat, onClose, onDelete }) {
  if (!record) return null;

  const colour = TYPE_COLOURS[record.recordType] || '#9b9b9b';
  const label = TYPE_LABELS[record.recordType] || record.recordType;
  const date = record.createdAt
    ? moment(record.createdAt).format(dateFormat)
    : '-';

  const title = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}
    >
      <Typography sx={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
        Risk Factor
      </Typography>
      <Typography sx={{ fontSize: '1.25rem' }}>- {date}</Typography>
      <Chip
        label={label}
        size="small"
        sx={{ backgroundColor: '#2BA9BF', color: '#fff', fontWeight: 700 }}
      />
    </Box>
  );

  return (
    <CustomDialog
      open
      title={title}
      handleClose={onClose}
      allowBackgroundClose
      maxWidth="sm"
      minHeight="auto"
      onClose
      showActions
      saveBtnLabel="Delete"
      onSave={onDelete}
      cancelBtnLabel="Close"
    >
      {record.recordType === 'gail' && <GailDetail record={record} />}
      {record.recordType === 'canrisk' && <CanRiskDetail record={record} />}
      {record.recordType === 'prs' && <PrsDetail record={record} />}
    </CustomDialog>
  );
}

function PrsDetail({ record }) {
  const result = record.prsResult;
  if (!result) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        No score recorded.
      </Typography>
    );
  }

  const rows = [
    ['PGS Catalog ID', result.pgsId],
    ['Model', result.pgsName],
    ['Trait', result.trait],
    ['Ancestry', result.ancestry],
    ['Z-score', result.zscore],
    ['Percentile', result.percentile != null ? `${result.percentile}%` : null],
    ['Relative risk / odds ratio', result.relativeRisk],
    [
      'Source',
      CONSTANTS.prsSourceOptions.find((o) => o.value === result.source)
        ?.label || 'Manually entered'
    ],
    // Only present for records from the "Compute from VCF" proof-of-concept
    // — a raw, unnormalised score, distinct from the z-score/percentile above.
    ['Raw score (unnormalised)', result.rawScore],
    [
      'Variant coverage',
      result.variantCoveragePct != null ? `${result.variantCoveragePct}%` : null
    ],
    ['Notes', result.notes]
  ].filter(([, v]) => v != null && v !== '');

  return (
    <Box>
      {rows.map(([k, v]) => (
        <DetailRow key={k} label={k} value={v} />
      ))}
      {result.publication?.title && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" gutterBottom>
            Publication
          </Typography>
          <Typography variant="body2">{result.publication.title}</Typography>
          {result.publication.pmid && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              PMID {result.publication.pmid}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

function DeleteConfirmModal({ record, dateFormat, onConfirm, onCancel }) {
  if (!record) return null;

  const label = TYPE_LABELS[record.recordType] || record.recordType;
  const date = record.createdAt
    ? moment(record.createdAt).format(dateFormat)
    : '—';

  return (
    <CustomDialog
      open
      title="Delete Risk Record"
      titleAppearance="warning"
      handleClose={onCancel}
      allowBackgroundClose
      maxWidth="xs"
      minHeight="auto"
      onClose
      showActions
      saveBtnLabel="Delete"
      onSave={onConfirm}
      cancelBtnLabel="Cancel"
    >
      <Typography>
        Are you sure you want to delete the <strong>{label}</strong> record from{' '}
        <strong>{date}</strong>? This cannot be undone.
      </Typography>
    </CustomDialog>
  );
}

function RecordTile({ record, isLatest, dateFormat, onClick }) {
  const colour = TYPE_COLOURS[record.recordType] || '#9b9b9b';
  const label = TYPE_LABELS[record.recordType] || record.recordType;
  const date = record.createdAt
    ? moment(record.createdAt).format(dateFormat)
    : '-';

  const canriskSummary =
    record.recordType === 'canrisk'
      ? getCanriskSummary(record.canriskResult)
      : null;

  return (
    <Box
      onClick={onClick}
      sx={{
        border: '1px solid #e0e0e0',
        borderLeft: `3px solid ${colour}`,
        borderRadius: '6px',
        padding: '8px 10px',
        backgroundColor: '#fff',
        mb: 1,
        cursor: 'pointer',
        '&:hover': { backgroundColor: '#fafafa' }
      }}
    >
      <Typography
        sx={{
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: colour,
          lineHeight: 1
        }}
      >
        Risk Score - {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {record.recordType === 'gail' && record.gailRiskScore != null && (
          <>Risk score: {record.gailRiskScore}</>
        )}
        {record.recordType === 'canrisk' && (
          <>
            {canriskSummary ||
              (record.canriskResult
                ? 'Scores calculated'
                : 'Pedigree downloaded')}
          </>
        )}
        {record.recordType === 'prs' && (
          <>
            {record.prsResult?.trait || record.prsResult?.pgsId}
            {getPrsSummary(record.prsResult)
              ? ` — ${getPrsSummary(record.prsResult)}`
              : ''}
          </>
        )}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.25 }}>
        {date}
      </Typography>
      {isLatest && (
        <Typography
          variant="body2"
          sx={{ fontStyle: 'italic', color: 'text.secondary', mt: 0.25 }}
        >
          Latest result
        </Typography>
      )}
    </Box>
  );
}

// A record counts as "scored" once a calculation has produced a result (or,
// for PRS, once a score has been entered — there's no in-app calculation).
const isScored = (r) =>
  (r.recordType === 'gail' && r.gailRiskScore != null) ||
  (r.recordType === 'canrisk' && r.canriskResult != null) ||
  (r.recordType === 'prs' &&
    (r.prsResult?.zscore != null || r.prsResult?.percentile != null));

export default function RiskRecordsList({
  userId,
  refreshKey,
  recordType,
  memberIds,
  scoredOnly = false
}) {
  const { theme } = React.useContext(ThemeContext);
  const dateFormat = theme?.dateFormat || 'DD/MM/YYYY';

  const [records, setRecords] = React.useState([]);
  const [selectedRecord, setSelectedRecord] = React.useState(null);
  const [recordToDelete, setRecordToDelete] = React.useState(null);

  const memberKey = (memberIds || []).filter(Boolean).map(String).join('|');

  React.useEffect(() => {
    if (!userId) return;
    const ids = memberKey ? memberKey.split('|') : [];
    axios
      .get(APICONSTANTS.getRiskRecords.replace(':user_id', userId))
      .then(({ data }) => {
        const all = Array.isArray(data) ? data : [];
        setRecords(
          all.filter(
            (r) =>
              (!recordType || r.recordType === recordType) &&
              (ids.length === 0 || ids.includes(String(r.familyMemberId))) &&
              (!scoredOnly || isScored(r))
          )
        );
      })
      .catch(() => {});
  }, [userId, refreshKey, recordType, memberKey, scoredOnly]);

  const handleDelete = () => {
    return axios
      .delete(APICONSTANTS.deleteRiskRecord.replace(':id', recordToDelete._id))
      .then(() => {
        setRecords((prev) => prev.filter((r) => r._id !== recordToDelete._id));
        setRecordToDelete(null);
      });
  };

  if (records.length === 0) return null;

  return (
    <>
      <Box
        sx={{
          mt: 1
        }}
      >
        {records.map((record, index) => (
          <RecordTile
            key={record._id}
            record={record}
            isLatest={index === 0}
            dateFormat={dateFormat}
            onClick={() => setSelectedRecord(record)}
          />
        ))}
      </Box>
      {selectedRecord && !recordToDelete && (
        <RecordDetailModal
          record={selectedRecord}
          dateFormat={dateFormat}
          onClose={() => setSelectedRecord(null)}
          onDelete={() => setRecordToDelete(selectedRecord)}
        />
      )}
      {recordToDelete && (
        <DeleteConfirmModal
          record={recordToDelete}
          dateFormat={dateFormat}
          onConfirm={handleDelete}
          onCancel={() => setRecordToDelete(null)}
        />
      )}
    </>
  );
}
